import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
// @mui
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  RadioGroup,
  Stack,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
// components
import DIOText from '../../../../components/DIOText';
import SvgIconStyle from '../../../../components/SvgIconStyle';
import Iconify from '../../../../components/Iconify';
// hooks
import useResponsive from '../../../../hooks/useResponsive';
import useAuth from '../../../../hooks/useAuth';
// util
import axios from '../../../../utils/axios';

const BUDGET_OPTIONS = [
  "100만원 이하",
  "100만원 ~ 200만원",
  "200만원 ~ 300만원",
  "300만원 ~ 400만원",
  "400만원 이상",
  "정해지지 않았음. 협의 가능.",
];

export default function SetMonthlyBudgetForm({ jobPost, next }) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const [isSubmit, setIsSubmit] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [budget, setBudget] = useState(null);
  const [openHelpDialog, setOpenHelpDialog] = useState(false);
  
  const handleError = (error) => {
    console.error(error);
    if (error.response && error.response.status === 400) {
      enqueueSnackbar(error.response.data.message, { variant: 'error' });
    } else {
      enqueueSnackbar('저장에 실패하였습니다. 관리자에 문의해주세요', { variant: 'error' });
    }
  };

  const setMonthlyBudget = async () => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    try {
      const jobPostId = jobPost.id;
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/monthly-budget/`, {
        monthly_budget: budget,
      });
      if (smUp) {
        enqueueSnackbar('저장되었습니다');
      } else {
        enqueueSnackbar('저장되었습니다', {anchorOrigin: { vertical: 'bottom', horizontal: 'center' }});
      }
      await next(jobPostId);
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      handleError(error);
    }
  };

  useEffect(() => {
    if (budget) {
      setCanSubmit(true);
    } else {
      setCanSubmit(false);
    }
  }, [budget]);

  useEffect(() => {
    if (jobPost.monthly_budget) {
      setBudget(jobPost.monthly_budget);
    }
  }, [jobPost]);

  return (
    <>
    <Typography variant="h4">
      DIO 프리랜서 한 명당 월 예산은 어느 정도로 생각하고 계신가요?
    </Typography>

    <Button
      type="button"
      size={smUp ? "medium" : "small"}
      sx={{ color: "text.secondary", width: "fit-content", mt: 0.5, ml: -0.5, mb: 1.5 }}
      onClick={() => setOpenHelpDialog(true)}
      startIcon={<Iconify icon={'eva:info-fill'} />}
    >
      월 예산 작성이 어려우신가요?
    </Button>

    <RadioGroup name="budgetRadio" value={budget} onChange={(e) => setBudget(e.target.value)}>
      {BUDGET_OPTIONS.map((option, index) => {
        return (
          <FormControlLabel
            sx={{
              "&:hover": {background: (theme) => theme.palette.grey[200]},
              background: (theme) => budget === option && theme.palette.grey[200],
              mx: 0,
              py: 1.5,
              px: 2,
              my: 1,
              border: (theme) => budget === null ? `1px solid ${theme.palette.grey[500_32]}` : budget === option ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
              borderRadius: 1,
              alignItems: "center",
              color: budget === null ? "text.primary" : budget === option ? "text.primary" : "text.disabled",
              "& .MuiFormControlLabel-label": { ...theme.typography.subtitle2 },
            }}
            key={index}
            value={option}
            control={<Radio sx={{ width: 20, height: 20, mr: 1.5 }} />}
            label={option}
          />
        )}
      )}
    </RadioGroup>

    <LoadingButton
      size="large"
      variant="contained"
      sx={{ my: 3 }}
      disabled={!canSubmit}
      onClick={setMonthlyBudget}
      loading={isSubmit}
    >
      다음
    </LoadingButton>

    <HelpDialog open={openHelpDialog} closeDialog={() => setOpenHelpDialog(false)} />

    </>
  )
}

function HelpDialog({ open, closeDialog }) {
  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="dg">
      {/* contents */}
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
        <Typography variant="h6" sx={{ wordBreak: "keep-all" }} gutterBottom>
          월 예산 작성 안내
        </Typography>
        <Typography variant="body2">
          공고 작성을 완료하시면 전담 매니저가 연락드릴 예정입니다. <br />
          금액 설정에 도움을 드리면서 이후 수정/협의 가능하니, 편하게 작성해주세요. <br />
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="flex-start">
          <SvgIconStyle src={`/icons/icon-clock-mono.svg`} sx={{ width: 20, height: 20, color: "text.disabled", minWidth: 20, minHeight: 20 }} />
          <Typography variant="body2">
            <b><DIOText /> 프리랜서는 보통 주 15~20시간</b> 일합니다. 작성하실 때 참고해주세요.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="flex-start">
          <SvgIconStyle src={`/icons/icon-check-circle-mono.svg`} sx={{ width: 20, height: 20, color: "text.disabled", minWidth: 20, minHeight: 20 }} />
          <Typography variant="body2">
            <b>예산은 이후 수정/협의 가능합니다.</b> 손해보지 않고 만족스러운 결과물을 얻으실 수 있도록, <DIOText /> 전담 매니저가 연락드리면서 금액 설정에 도움을 드릴 예정입니다.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="flex-start">
          <Iconify icon={'eva:alert-circle-fill'} sx={{ width: 20, height: 20, color: "text.disabled", minWidth: 20, minHeight: 20 }} />
          <Typography variant="body2">
            <b>예산 범위가 너무 적으면</b> 공고가 실력자들의 선택을 받지 못할 수도 있습니다. 가능한만큼 선택해주세요.
          </Typography>
        </Stack>
      </DialogContent>
      {/* buttons */}
      <DialogActions sx={{ p: { xs: 3, sm: 4 } }}>
        <Button
          onClick={closeDialog}
          size="large"
          sx={{ px: 5 }}
          variant="contained"
        >
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  )
}
